/**
 * Single-instance lock — one recording tab per interview.
 *
 * THE PROBLEM (handoff §8.3). The recording socket (plane 3) supersedes: a new
 * connection closes the older one with 4000. The conversation socket (plane 2)
 * does NOT — confirmed with backend, two live conversation sockets for the same
 * interview are permitted. So with two tabs open you can get **events from tab A
 * stitched onto video from tab B**: A's `part_index` refers to a part sequence
 * that never reached disk, markers land at plausible-but-wrong positions, and
 * nothing errors anywhere.
 *
 * THE NEWER TAB WINS, and the direction is load-bearing. It matches plane 3's
 * supersession: the newest tab is the one holding the live recording socket.
 * Favouring the older tab would leave a winning socket attached to a UI that has
 * stood down and refuses to record — an interview that looks fine and produces
 * nothing.
 *
 * WHAT THIS IS NOT. It is not airtight, and it is not meant to be: `BroadcastChannel`
 * is same-origin and same-browser-profile, so a second tab in another browser, or
 * in incognito, is invisible to it. That is exactly why `session_instance_id`
 * exists on every event (see eventIdentity.ts) — the lock reduces the occurrence,
 * and the per-event id lets the review player detect genuine temporal overlap when
 * the lock could not. Both layers are needed; neither replaces the other.
 */

/** The slice of BroadcastChannel we use. Narrow so a test can stand in for it. */
export interface TabLockChannel {
  postMessage: (message: unknown) => void;
  close: () => void;
  onmessage: ((event: { data: unknown }) => void) | null;
}

interface ClaimMessage {
  type: "interview-tab-claim";
  instanceId: string;
  claimedAt: number;
}

function isClaim(data: unknown): data is ClaimMessage {
  const m = data as ClaimMessage | null;
  return (
    !!m &&
    m.type === "interview-tab-claim" &&
    typeof m.instanceId === "string" &&
    typeof m.claimedAt === "number"
  );
}

export interface TabLockOptions {
  interviewId: string | number;
  /**
   * This page load's identity. Pass `getSessionInstanceId()` so the lock and the
   * events on the wire agree about who this tab is — that is what lets a reviewer
   * line a stand-down up against the events either tab produced.
   */
  instanceId: string;
  /** Called on the OLDER tab when a newer one claims the interview. */
  onSuperseded: () => void;
  /** Test seam. Defaults to a real BroadcastChannel where the browser has one. */
  createChannel?: (name: string) => TabLockChannel | null;
  /** Injectable for deterministic ordering in tests. */
  now?: () => number;
}

// ── "Is an interview being recorded in THIS tab?" ───────────────────────────
// Read by authSync so a login/logout made in another tab never tears this tab
// down mid-recording; it is applied once the last lock here is released.
let heldLocks = 0;
const lockChangeListeners = new Set<() => void>();

export function isInterviewTabLockHeld(): boolean {
  return heldLocks > 0;
}

/** Notified whenever a lock in this tab is taken or let go. Returns unsubscribe. */
export function onInterviewTabLockChange(fn: () => void): () => void {
  lockChangeListeners.add(fn);
  return () => lockChangeListeners.delete(fn);
}

function adjustHeldLocks(delta: 1 | -1) {
  heldLocks = Math.max(0, heldLocks + delta);
  lockChangeListeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* a listener must not break the lock */
    }
  });
}

function defaultCreateChannel(name: string): TabLockChannel | null {
  try {
    // Absent in older Safari and in some embedded webviews. No lock is strictly
    // better than a thrown error during interview setup — the session_instance_id
    // layer still detects overlap after the fact.
    const Ctor = (globalThis as { BroadcastChannel?: new (n: string) => TabLockChannel })
      .BroadcastChannel;
    if (typeof Ctor !== "function") return null;
    return new Ctor(name);
  } catch {
    return null;
  }
}

/**
 * Claim this interview for this tab.
 *
 * Returns a release function. Idempotent to call release more than once.
 *
 * No handshake and no waiting: claiming IS winning, because the newest claim
 * always takes precedence. A tab only ever hears about claims made after it
 * started listening, which is precisely the set of tabs newer than itself —
 * BroadcastChannel does not replay history, and that asymmetry is what makes the
 * one-way rule sufficient.
 */
export function acquireInterviewTabLock({
  interviewId,
  instanceId,
  onSuperseded,
  createChannel = defaultCreateChannel,
  now = Date.now,
}: TabLockOptions): () => void {
  // Counted even without a channel: the interview is still being recorded here.
  adjustHeldLocks(1);
  let counted = true;
  const uncount = () => {
    if (!counted) return;
    counted = false;
    adjustHeldLocks(-1);
  };

  const channel = createChannel(`interview-tab-lock:${interviewId}`);
  if (!channel) return uncount;

  const claimedAt = now();
  let released = false;
  let superseded = false;

  channel.onmessage = (event) => {
    if (released || superseded) return;
    const data = event?.data;
    if (!isClaim(data)) return;
    if (data.instanceId === instanceId) return;

    // Stand down only for a STRICTLY newer claim. The instanceId tiebreak covers
    // two tabs opened inside the same millisecond, which would otherwise both
    // ignore each other and both keep recording — the exact state the lock exists
    // to prevent.
    const incomingIsNewer =
      data.claimedAt > claimedAt ||
      (data.claimedAt === claimedAt && data.instanceId > instanceId);
    if (!incomingIsNewer) return;

    superseded = true;
    uncount(); // this tab has stood down — it is no longer recording
    try {
      onSuperseded();
    } finally {
      try {
        channel.close();
      } catch {
        /* already closing */
      }
    }
  };

  const claim: ClaimMessage = {
    type: "interview-tab-claim",
    instanceId,
    claimedAt,
  };
  try {
    channel.postMessage(claim);
  } catch {
    // A failed announce means older tabs keep recording. Degraded, not broken —
    // and still detectable through session_instance_id overlap.
  }

  return () => {
    uncount();
    if (released) return;
    released = true;
    try {
      channel.close();
    } catch {
      /* ignore */
    }
  };
}
